/* eslint max-len:0 */
import React, { useState, useEffect, useMemo } from 'react';
import { withStyles, ListItemText } from '@material-ui/core';
import { Search as SearchIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MSearchInput from '~/components/PF2MSearchInput';
import Footer from '~/pages/Manager/Footer';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import {
  getSystems,
  getSystemItems,
  getFailureModes,
  getFailureModesSystemItems,
  setFailureModeSystemItemValue,
  updateFailureModesSystemItems,
  resetFailureModesSystemItems,
  getFailureModeEquipmentGroupMaintenanceItems,
  getEquipmentGroupsSystemItems,
} from '~/store/maintenance/actions';

import {
  getEquipmentTypes,
  getEquipmentsGroups,
} from '~/store/manager/actions';

import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MScrollbar from '~/components/PF2MScrollbar';

const FailureModeSystemItemsTab = ({ classes }) => {
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedSystem, setSelectedSystem] = useState(0);
  const [selectedSystemItem, setSelectedSystemItem] = useState(0);
  const [selectedEquipmentType, setSelectedEquipmentType] = useState(0);
  const [selectedEquipmentGroup, setSelectedEquipmentGroup] = useState(0);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();

  const isFailureModesSystemItemsDirty = useSelector(state => state.maintenance.isFailureModesSystemItemsDirty);
  const systems = useSelector(state => state.maintenance.systems);
  const failureModes = useSelector(state => state.maintenance.failureModes);
  const failureModesSystemItems = useSelector(state => state.maintenance.failureModesSystemItems);
  const equipmentTypes = useSelector(state => state.manager.equipmentTypes);
  const equipmentsGroups = useSelector(state => state.manager.equipmentsGroups);
  const failureModeEquipmentGroup = useSelector(state => state.maintenance.failureModeEquipmentGroup);
  const equipmentGroupsSystemItems = useSelector(state => state.maintenance.equipmentGroupsSystemItems);

  const filtersReady = selectedSystemItem > 0;


  useEffect(() => {
    async function fetchData() {
      dispatch(getSystemItems());
      dispatch(getSystems());
      dispatch(getFailureModes());
      dispatch(getFailureModesSystemItems());
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentsGroups());
      dispatch(getFailureModeEquipmentGroupMaintenanceItems());
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  useEffect(() => {
    if (selectedEquipmentGroup && selectedSystem) {
      dispatch(getEquipmentGroupsSystemItems(selectedEquipmentGroup, selectedSystem));
    }
  }, [selectedEquipmentGroup, selectedSystem, dispatch]);

  /* funcao resposavel por obter somente os modos de falhas que existem na tabela
  failure mode equipment group */
  const failureModeSystemItemIds = useMemo(
    () => new Set(failureModesSystemItems.map(f => f.id)),
    [failureModesSystemItems],
  );

  const selectedEquipmentFailureGroup = useMemo(
    () => failureModeEquipmentGroup.filter(
      e => e.equipment_group_id === selectedEquipmentGroup
        && failureModeSystemItemIds.has(e.id_failure_mode_system_item),
    ),
    [failureModeEquipmentGroup, selectedEquipmentGroup, failureModeSystemItemIds],
  );

  const sortedFailureModes = useMemo(
    () => [...failureModes].sort((a, b) => a.name.localeCompare(b.name)),
    [failureModes],
  );

  const filteredFailureModes = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) return sortedFailureModes;
    return sortedFailureModes.filter(item => item.name?.toLowerCase().includes(term));
  }, [sortedFailureModes, searchText]);

  const checkedFailureModeIds = useMemo(() => {
    const selectedEquipGroupIds = new Set(
      selectedEquipmentFailureGroup.map(e => e.id_failure_mode_system_item),
    );
    const hasEquipGroupFilter = selectedEquipmentFailureGroup.length > 0;
    const checked = new Set();

    failureModesSystemItems.forEach((systemItem) => {
      if (
        systemItem.id_system_item_maintenance !== selectedSystemItem
        || systemItem.update_status === 'D'
      ) {
        return;
      }
      if (hasEquipGroupFilter && !selectedEquipGroupIds.has(systemItem.id)) {
        return;
      }
      checked.add(systemItem.id_failure_mode_maintenance);
    });

    return checked;
  }, [failureModesSystemItems, selectedSystemItem, selectedEquipmentFailureGroup]);

  const renderFailureModesSystemItems = () => filteredFailureModes.map(failureMode => (
    <div key={failureMode.id} style={{ display: 'flex', alignItems: 'center' }}>
      <input
        type="checkbox"
        checked={checkedFailureModeIds.has(failureMode.id)}
        color="primary"
        name={failureMode.id.toString()}
        onChange={e => dispatch(
          setFailureModeSystemItemValue(
            selectedSystemItem, failureMode.id, selectedEquipmentGroup, e.target.checked,
          ),
        )}
        disabled={selectedSystemItem === 0}
      />
      <ListItemText secondary={failureMode.name} style={{ padding: 0, paddingLeft: 5 }} />
    </div>
  ));

  const handleEquipmentType = (e) => {
    const selected = equipmentTypes.some(m => m.update_status);
    if (selected) {
      setIsAlertOpen(true);
    } else {
      setSelectedSystemItem(0);
      setSelectedSystem(0);
      setSelectedEquipmentGroup(0);
      setSelectedEquipmentType(e.target.value);
      setSearchText('');
    }
  };

  const handleEquipmentGroup = (e) => {
    const selected = equipmentsGroups.some(m => m.update_status);
    if (selected) {
      setIsAlertOpen(true);
    } else {
      setSelectedSystemItem(0);
      setSelectedSystem(0);
      setSelectedEquipmentGroup(e.target.value);
      setSearchText('');
    }
  };

  const handleSystemChange = (e) => {
    const selected = failureModesSystemItems.some(m => m.update_status);
    if (selected) {
      setIsAlertOpen(true);
    } else {
      setSelectedSystemItem(0);
      setSelectedSystem(e.target.value);
      setSearchText('');
    }
  };

  const handleSystemItemChange = (e) => {
    const selected = failureModesSystemItems.some(m => m.update_status);
    if (selected) {
      setIsAlertOpen(true);
    } else {
      setSelectedSystemItem(e.target.value);
      setSearchText('');
    }
  };


  return (
    <div className={classes.tabContainer}>
      <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
        <div style={{ marginRight: 10 }}>
          <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
            <div className={classes.formLabel}>
              {translate('common:EquipmentType')}
            </div>
            <PF2MSearchSelectOutlined
              value={selectedEquipmentType}
              onChange={handleEquipmentType}
              fullWidth={false}
              className={classes.typeSelector}
            >
              {[{ value: 0, label: translate('common:Select') }]
                .concat(...equipmentTypes
                  .map(e => ({ value: e.id, label: e.name })))}
            </PF2MSearchSelectOutlined>
          </div>
        </div>
        <div style={{ marginRight: 10 }}>
          <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
            <p className={classes.formLabel}>
              {translate('common:EquipmentGroup')}
            </p>
            <PF2MSearchSelectOutlined
              value={selectedEquipmentGroup}
              onChange={handleEquipmentGroup}
              fullWidth={false}
              className={classes.typeSelector}
            >
              {[{ value: 0, label: translate('common:Select') }]
                .concat(...equipmentsGroups
                  .filter(e => selectedEquipmentType === 0 || e.id_equipament === selectedEquipmentType)
                  .map(e => ({ value: e.id, label: e.name })))}
            </PF2MSearchSelectOutlined>
          </div>
        </div>
        <div style={{ marginRight: 10 }}>
          <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
            <p className={classes.formLabel}>
              {`${translate('common:FilterSystem')}:`}
            </p>
            <PF2MSearchSelectOutlined
              value={selectedSystem}
              onChange={handleSystemChange}
              fullWidth={false}
              className={classes.typeSelector}
            >
              {[{ value: 0, label: translate('common:Select') }]
                .concat(...systems
                  .map(e => ({ value: e.id, label: e.name })))}
            </PF2MSearchSelectOutlined>
          </div>
        </div>
        <div style={{ marginRight: 10 }}>
          <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
            <p className={classes.formLabel}>
              {`${translate('common:FilterSystemItem')}:`}
            </p>
            <PF2MSearchSelectOutlined
              value={selectedSystemItem}
              onChange={handleSystemItemChange}
              fullWidth={false}
              className={classes.typeSelector}
            >
              {[{ value: 0, label: translate('common:Select') }]
                .concat(...equipmentGroupsSystemItems
                  .filter(f => f.active === 1)
                  .map(e => ({
                    value: e.system_item_maintenance_id,
                    label: e.system_item_maintenance_name,
                  })))}
            </PF2MSearchSelectOutlined>
          </div>
        </div>
        {filtersReady && failureModes.length > 0 && (
          <div style={{ marginLeft: 'auto', alignSelf: 'flex-end' }}>
            <div className={classes.containerTypeSelector} style={{ flexDirection: 'column' }}>
              <PF2MSearchInput
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                placeholder={translate('common:Find')}
                startIcon={<SearchIcon fontSize="small" color="primary" />}
              />
            </div>
          </div>
        )}
      </div>
      <div className={classes.listArea}>
        <span className={classes.formLabel}>
          {`${translate('common:FailureModes')}:`}
        </span>
        <div style={{ flex: 1, minHeight: 0 }}>
          <PF2MScrollbar className={classes.scrollbars}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)' }}>
              {renderFailureModesSystemItems()}
            </div>
          </PF2MScrollbar>
        </div>
      </div>
      <div style={{ marginTop: 20 }}>
        <Footer
          isDirty={isFailureModesSystemItemsDirty}
          discard={() => dispatch(resetFailureModesSystemItems())}
          sendData={() => dispatch(
            updateFailureModesSystemItems(failureModesSystemItems, failureModeEquipmentGroup),
          )}
          style={{ marginTop: 10 }}
        />
      </div>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:YouHaveUnsavedData')}
        open={isAlertOpen}
        onConfirm={() => setIsAlertOpen(false)}
      />
    </div>
  );
};

FailureModeSystemItemsTab.propTypes = {
  classes: PropTypes.object.isRequired,
};

FailureModeSystemItemsTab.defaultProps = {};

export default withStyles(styles)(FailureModeSystemItemsTab);
